<template>
  <section class="unlock-riddle">
    <div class="candy-bar spinning" />

    <h1 class="unlock-game">Unlock a Game!</h1>
    <h4>Solve the riddle to play!</h4>

    <p>Which pic is on your sticker?</p>

    <div class="carousel container">
      <CJCarousel
        ref="carousel"
        :items="stickerSet"
      >
        <template #item="{ id }">
          <CJSticker
            :sticker="id"
            :selected="id === selectedSticker"
            :key="id"
            @click="stickerClicked($event, id)"
          />
        </template>
      </CJCarousel>

      <CJToast ref="toast" :success="!!toast" @hide="toastHidden" />
    </div>

    <div class="riddle" v-if="isStickerValid">
      <p>Now pick the pic that completes<br />this word or phrase.</p>

      <div class="completion-name">
        {{ validCompletion.name }}
      </div>

      <div class="completions">
        <CJSticker
          :id="c.valid && 'correct-pic'"
          :data-word="c.id"
          :sticker="c.id"
          :selected="c.id === selectedCompletion"
          v-for="c in stickerCompletions"
          :key="c.id"
          @click="selectCompletion(c.id)"
        />
      </div>
    </div>

    <div class="blue-screen" />
  </section>
</template>

<script>
import { tween, styler, easing } from 'popmotion'
import {
  compose,
  concat,
  filter,
  find,
  head,
  not,
  pipe,
  prop,
  take,
} from 'ramda'

import { shuffle } from '@/util/function'
import CJCarousel from '@/components/CJCarousel'
import CJSticker from '@/components/CJSticker'
import CJToast from '@/components/CJToast'

const filterValid = filter(prop('valid'))
const filterInvalid = filter(compose(not, prop('valid')))
const selectAdditional = pipe(filterInvalid, shuffle, take(6))

export default {
  name: 'UnlockRiddle',

  components: { CJCarousel, CJSticker, CJToast },

  data () {
    const stickers = [
      { id: 'dog', valid: true },
      { id: 'jack', valid: true },
      { id: 'baseball', valid: true },
      { id: 'pop', valid: true },
      { id: 'cracker-jack' },
      { id: 'peanut' },
      { id: 'bell' },
      { id: 'fence' },
      { id: 'hat' },
      { id: 'love' },
      { id: 'tongue' },
      { id: 'sun' },
      { id: 'guitar' },
      { id: 'car' },
    ]

    const completions = {
      dog: [
        { id: 'watch-dog', name: 'Watch Dog' },
        { id: 'hot-dog', name: 'Hot Dog' },
        { id: 'dog-house', name: 'Dog House' },
        { id: 'dog-bone', name: 'Dog Bone' },
      ],

      jack: [
        { id: 'jack-hammer', name: 'Jack Hammer' },
        { id: 'jack-rabbit', name: 'Jack Rabbit' },
        { id: 'jack-o-lantern', name: 'Jack O’ Lantern' },
        { id: 'jack-pot', name: 'Jack Pot' },
      ],

      baseball: [
        { id: 'baseball-hat', name: 'Baseball Hat' },
        { id: 'baseball-glove', name: 'Baseball Glove' },
        { id: 'baseball-bat', name: 'Baseball Bat' },
        { id: 'baseball-diamond', name: 'Baseball Diamond' },
      ],

      pop: [
        { id: 'pop-star', name: 'Pop Star' },
        { id: 'pop-fly', name: 'Pop Fly' },
        { id: 'pop-music', name: 'Pop Music' },
        { id: 'pop-corn', name: 'Pop Corn' },
      ],
    }

    for (let key in completions) {
      pipe(shuffle, head)(completions[key]).valid = true
    }

    return {
      selectedSticker: null,
      selectedCompletion: null,
      toast: null,
      stickers,
      completions,
    }
  },

  computed: {
    stickerSet () {
      return shuffle(concat(
        filterValid(this.stickers),
        selectAdditional(this.stickers),
      ))
    },

    stickerCompletions () {
      return this.selectedSticker
        ? this.completions[this.selectedSticker]
        : []
    },

    validCompletion () {
      return find(prop('valid'), this.stickerCompletions)
    },

    isStickerValid () {
      return this.selectedSticker
        ? find(x => x.id === this.selectedSticker, this.stickers).valid
        : false
    },

    isCompletionValid () {
      return !this.selectedCompletion
        ? false
        : find(x => x.id === this.selectedCompletion, this.stickerCompletions)
          .valid
    },
  },

  methods: {
    /** @param {Event} event */
    stickerClicked (event, id) {
      this.selectSticker(id)
      this.$refs.carousel.scrollIntoView(event.currentTarget)
    },

    selectSticker (id) {
      if (this.$refs.toast.animating) return

      this.selectedSticker = id
      this.toast = this.isStickerValid

      if (!this.toast) this.$refs.toast.show()
    },

    selectCompletion (id) {
      if (this.$refs.toast.animating) return

      this.selectedCompletion = id
      this.toast = this.isCompletionValid
      this.$refs.toast.show()

      if (this.isCompletionValid) {
        this.$store.dispatch('inputCode', this.selectedCompletion)
      }
    },

    toastHidden () {
      if (!this.isStickerValid) {
        this.selectedSticker = null
      }

      if (!this.isCompletionValid) {
        this.selectedCompletion = null
      } else {
        this.goNext()
      }

      this.toast = null
    },

    goNext () {
      const blueScreen = this.$el.querySelector('.blue-screen')
      const blueScreenStyler = styler(blueScreen)

      const candyBar = this.$el.querySelector('.candy-bar')
      const candyBarStyler = styler(candyBar)

      const scaleX = window.innerWidth < 1024 ? 1.045 : 1.017

      tween({
        to: { height: 595 },
        duration: 1000,
        ease: easing.easeInOut,
      }).start(v => blueScreenStyler.set(v))

      candyBar.classList.remove('spinning')
      tween({
        from: { x: 0, y: 0, scaleX: 1 },
        to: { x: -1, y: 595, scaleX },
        duration: 1000,
        ease: easing.easeInOut,
      }).start({
        update: v => { candyBarStyler.set(v) },
        complete: () => this.$router.push('/unlock/games'),
      })
    },
  },
}
</script>

<style lang="stylus" scoped>
  section.unlock-riddle
    display: flex
    flex-direction: column
    align-items: center
    width: 100%
    min-height: 100%
    text-align: center
    font-weight: 700
    font-size: 26px
    font-family: 'Futura Std Condensed'

    p
      margin: 0

    h1.unlock-game
      flex: none
      margin: 40px 0 10px

    h4
      margin: 0 0 3rem
      text-transform: uppercase

      @media screen and (min-device-width: 1024px)
        margin: 0 0 1rem

    .carousel
      position: relative
      margin: 0 0 30px
      width: calc(100% - 10px)
      max-width: 510px

      .cj-carousel
        width: 100%

      .cj-toast
        position: absolute
        top: 0
        left: 50%
        transform: translateX(-50%)

    .completions
      display: flex
      justify-content: center

    .completion-name
      margin: 12px 0 24px
      padding: 9px 64px 3px
      background-color: rgba(255, 255, 255, 0.7)
      color: #15488d
      text-transform: uppercase
      font-size: 27px
      line-height: 1

    .blue-screen
      position: absolute
      top: 0
      right: 0
      left: 0
      height: 0

    #correct-pic >>> > *
      pointer-events: none
</style>
